import React from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, DIVISIONS } from "@/constants/site";
import { ArrowRight, CheckCircle2, Wrench } from "lucide-react";
import useSeo from "@/hooks/useSeo";
import { breadcrumbSchema } from "@/lib/schema";
import { useQuote } from "@/components/QuoteProvider";

export default function ProductsIndexPage() {
  const { openQuote } = useQuote();
  useSeo({
    title: "Products — NDT Materials, Equipment, KLYDE Zinc & Industrial Bearings | SSK Enterprises",
    description:
      "Browse SSK Enterprises product categories: Dye Penetrant Testing, Magnetic Particle Inspection, NDT Equipment & Accessories, Test & Reference Blocks, KLYDE Zinc Materials and Industrial Bearings — supplied across Telangana and Andhra Pradesh.",
    path: "/products",
    jsonLd: [breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Products", path: "/products" }])],
  });

  const cats = Object.values(CATEGORIES);

  return (
    <>
      <section className="bg-[#F8FAFC] border-b border-slate-200">
        <div className="container-x py-16 md:py-20">
          <h1 className="font-display text-4xl md:text-5xl font-black text-[#1E293B] leading-tight max-w-3xl">
            Explore our product range
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl leading-relaxed">
            Six specialized product categories covering NDT consumables, NDT inspection equipment, reference test blocks, zinc protection systems and industrial bearings.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="category-grid">
            {cats.map((c, i) => (
              <Link
                key={c.slug}
                to={`/products/${c.slug}`}
                className="tech-card group relative flex flex-col overflow-hidden"
                data-testid={`category-card-${c.slug}`}
              >
                <div className="division-stripe" style={{ background: "#0F2747" }} />
                <div className={`aspect-[16/10] overflow-hidden ${c.image && c.image.endsWith('.svg') ? 'bg-slate-50 flex items-center justify-center p-4' : 'bg-slate-100'}`}>
                  <img
                    src={c.image}
                    alt={c.label}
                    loading="lazy"
                    decoding="async"
                    className={c.image && c.image.endsWith('.svg')
                      ? "w-full h-full object-contain"
                      : "w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"}
                  />
                </div>
                <div className="p-7 flex flex-col flex-grow">
                  <div className="text-[11px] font-mono-tech tracking-[0.18em] uppercase text-slate-500">
                    Category {String(i + 1).padStart(2, "0")} · {c.divisionLabel || DIVISIONS[c.division].short}
                  </div>
                  <h2 className="font-display text-2xl font-bold text-[#1E293B] mt-2 leading-tight">{c.label}</h2>
                  <p className="text-slate-600 mt-2 text-sm">{c.tagline}</p>
                  <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-600">
                    {c.includes.map((it) => (
                      <li key={it} className="flex gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-[#B5121B] flex-shrink-0 mt-0.5" />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-4 border-t border-slate-100 inline-flex items-center gap-2 text-[#0F2747] font-semibold text-sm">
                    View Products <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}

            {/* Bearings — separate enquiry-only card */}
            <Link
              to="/products/industrial-bearings"
              className="tech-card group relative flex flex-col overflow-hidden"
              data-testid="category-card-industrial-bearings"
            >
              <div className="division-stripe" style={{ background: "#1E293B" }} />
              <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src="/products/cat-bearings.jpg"
                  alt="Industrial bearings — ball, roller, tapered and spherical"
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-7 flex flex-col flex-grow">
                <div className="text-[11px] font-mono-tech tracking-[0.18em] uppercase text-slate-500">
                  Enquiry-Based · Industrial Bearings
                </div>
                <h2 className="font-display text-2xl font-bold text-[#1E293B] mt-2 leading-tight">Industrial Bearings</h2>
                <p className="text-slate-600 mt-2 text-sm">NTN and multi-brand bearings sourced on customer requirement</p>
                <ul className="mt-4 grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs text-slate-600">
                  {["Ball Bearings", "Roller Bearings", "Tapered Roller", "Spherical Roller", "Thrust Bearings", "Pillow Block Units"].map((it) => (
                    <li key={it} className="flex gap-1.5">
                      <CheckCircle2 className="w-3.5 h-3.5 text-[#B5121B] flex-shrink-0 mt-0.5" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-6 pt-4 border-t border-slate-100 inline-flex items-center gap-2 text-[#0F2747] font-semibold text-sm">
                  Start Bearing Enquiry <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          </div>

          <div className="mt-16 tech-card p-8 flex flex-col md:flex-row items-center justify-between gap-6" data-testid="products-help-cta">
            <div className="flex gap-4 items-start">
              <div className="w-12 h-12 bg-[#0F2747] text-white flex items-center justify-center flex-shrink-0">
                <Wrench className="w-6 h-6" />
              </div>
              <div>
                <h3 className="font-display text-xl font-black text-[#1E293B]">Not sure which product you need?</h3>
                <p className="text-slate-600 mt-1 text-sm">Our team will help you identify the right MR Chemie, KLYDE or bearing product for your application — with technical guidance and prompt quotations.</p>
              </div>
            </div>
            <button onClick={() => openQuote()} className="btn-primary flex-shrink-0" data-testid="help-cta-quote">
              Request a Quotation <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </>
  );
}
